/**
 * DatasetManager — Gestión de datasets para Data Driven Testing.
 *
 * Funciones:
 *  - Upload Excel → parseo en browser con SheetJS → POST al backend
 *  - Tabla editable: doble clic en celda → input inline → onBlur → PATCH /cell
 *  - Añadir / eliminar filas manualmente
 *  - Eliminar dataset completo
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { datasetApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Dataset {
  id: string;
  scenario_id: string;
  name: string;
  columns: string[];
  rows: string[][];
  row_count: number;
  active_rows: boolean[];
  active_row_count: number;
}

interface Props {
  scenarioId: string;
  scenarioTitle: string;
  onClose: () => void;
  onDatasetChange?: (hasDataset: boolean, rowCount: number, columns?: string[]) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function DatasetManager({ scenarioId, scenarioTitle, onClose, onDatasetChange }: Props) {
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Celda en edición: { row, col }
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState<{ row: number; col: number } | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);

  const notify = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  // ─── Load ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    setLoading(true);
    datasetApi.getDataset(scenarioId)
      .then(data => setDataset(data as Dataset | null))
      .catch(() => notify('Error al cargar dataset', false))
      .finally(() => setLoading(false));
  }, [scenarioId]);

  useEffect(() => {
    onDatasetChange?.(!!dataset, dataset?.row_count ?? 0, dataset?.columns ?? []);
  }, [dataset]);

  // ─── Upload Excel ──────────────────────────────────────────────────────────

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

      if (!raw || raw.length < 2) {
        notify('El archivo debe tener al menos una fila de encabezados y una de datos', false);
        return;
      }

      const columns = (raw[0] as string[]).map(c => String(c).trim()).filter(Boolean);
      if (!columns.length) { notify('No se detectaron columnas', false); return; }

      const rows = raw.slice(1).map(row =>
        columns.map((_, ci) => String((row as unknown[])[ci] ?? ''))
      );

      setSaving(true);
      const result = await datasetApi.uploadDataset(scenarioId, {
        name: file.name.replace(/\.[^.]+$/, ''),
        columns,
        rows,
      }) as unknown as Dataset;
      setDataset(result);
      notify(`Dataset cargado: ${columns.length} columnas, ${rows.length} filas`);
    } catch {
      notify('Error al procesar el archivo Excel', false);
    } finally {
      setSaving(false);
    }
  }, [scenarioId]);

  // ─── Inline cell editing ───────────────────────────────────────────────────

  const startEdit = (row: number, col: number, current: string) => {
    setEditingCell({ row, col });
    setEditValue(current);
  };

  const commitEdit = useCallback(async (row: number, col: number, value: string) => {
    if (!dataset) return;
    const column = dataset.columns[col];
    const original = dataset.rows[row]?.[col] ?? '';
    if (value === original) { setEditingCell(null); return; }

    setSavingCell({ row, col });
    setEditingCell(null);

    // Optimistic update
    const newRows = dataset.rows.map((r, ri) =>
      ri === row ? r.map((c, ci) => ci === col ? value : c) : r
    );
    setDataset(prev => prev ? { ...prev, rows: newRows } : prev);

    try {
      await datasetApi.updateDatasetCell(scenarioId, { row_index: row, column, value });
    } catch {
      // Revert
      setDataset(prev => prev ? { ...prev, rows: dataset.rows } : prev);
      notify('Error al guardar celda', false);
    } finally {
      setSavingCell(null);
    }
  }, [dataset, scenarioId]);

  const handleCellKeyDown = (e: React.KeyboardEvent, row: number, col: number) => {
    if (e.key === 'Enter') commitEdit(row, col, editValue);
    if (e.key === 'Escape') setEditingCell(null);
    if (e.key === 'Tab') {
      e.preventDefault();
      commitEdit(row, col, editValue);
      const nextCol = col + 1 < (dataset?.columns.length ?? 0) ? col + 1 : 0;
      const nextRow = col + 1 < (dataset?.columns.length ?? 0) ? row : row + 1;
      if (nextRow < (dataset?.rows.length ?? 0)) {
        setTimeout(() => startEdit(nextRow, nextCol, dataset?.rows[nextRow]?.[nextCol] ?? ''), 50);
      }
    }
  };

  // ─── Add row ───────────────────────────────────────────────────────────────

  const addRow = async () => {
    if (!dataset) return;
    const emptyRow = dataset.columns.map(() => '');
    const newRows = [...dataset.rows, emptyRow];
    const newActiveRows = [...dataset.active_rows, true];
    setSaving(true);
    try {
      const updated = await datasetApi.uploadDataset(scenarioId, {
        name: dataset.name,
        columns: dataset.columns,
        rows: newRows,
        active_rows: newActiveRows,
      }) as unknown as Dataset;
      setDataset(updated);
    } catch {
      notify('Error al agregar fila', false);
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete row ────────────────────────────────────────────────────────────

  const deleteRow = async (rowIdx: number) => {
    if (!dataset) return;
    const newRows = dataset.rows.filter((_, i) => i !== rowIdx);
    const newActiveRows = dataset.active_rows.filter((_, i) => i !== rowIdx);
    setSaving(true);
    try {
      const updated = await datasetApi.uploadDataset(scenarioId, {
        name: dataset.name,
        columns: dataset.columns,
        rows: newRows,
        active_rows: newActiveRows,
      }) as unknown as Dataset;
      setDataset(updated);
    } catch {
      notify('Error al eliminar fila', false);
    } finally {
      setSaving(false);
    }
  };

  // ─── Toggle row active ──────────────────────────────────────────────────────

  const toggleRowActive = async (rowIdx: number) => {
    if (!dataset) return;
    const nextActive = !dataset.active_rows[rowIdx];
    const newActiveRows = dataset.active_rows.map((a, i) => i === rowIdx ? nextActive : a);
    setDataset(prev => prev ? { ...prev, active_rows: newActiveRows, active_row_count: newActiveRows.filter(Boolean).length } : prev);
    try {
      await datasetApi.updateDatasetRowActive(scenarioId, { row_index: rowIdx, active: nextActive });
    } catch {
      setDataset(prev => prev ? { ...prev, active_rows: dataset.active_rows, active_row_count: dataset.active_row_count } : prev);
      notify('Error al actualizar la fila', false);
    }
  };

  // ─── Delete dataset ────────────────────────────────────────────────────────

  const [confirmDelete, setConfirmDelete] = useState(false);
  const handleDeleteDataset = async () => {
    setSaving(true);
    try {
      await datasetApi.deleteDataset(scenarioId);
      setDataset(null);
      setConfirmDelete(false);
      notify('Dataset eliminado');
    } catch {
      notify('Error al eliminar dataset', false);
    } finally {
      setSaving(false);
    }
  };

  // ─── Styles ────────────────────────────────────────────────────────────────

  const S = {
    overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
    modal: { background: 'var(--color-background-primary)', borderRadius: 14, width: '100%', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column' as const, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
    header: { padding: '18px 24px', borderBottom: '1px solid var(--color-border-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
    body: { padding: 24, overflow: 'auto', flex: 1 },
    btn: { padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
    btnPrimary: { background: '#185FA5', color: '#fff' },
    btnDanger: { background: '#dc3545', color: '#fff' },
    btnGhost: { background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-secondary)' },
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>

        {/* Header */}
        <div style={S.header}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>
              📊 Dataset — {scenarioTitle}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
              {dataset ? `${dataset.columns.length} columnas · ${dataset.row_count} filas` : 'Sin dataset configurado'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {dataset && !confirmDelete && (
              <button onClick={() => setConfirmDelete(true)} style={{ ...S.btn, ...S.btnDanger }}>
                🗑 Eliminar dataset
              </button>
            )}
            {confirmDelete && (
              <>
                <span style={{ fontSize: 12, color: '#dc3545', fontWeight: 600 }}>¿Confirmar eliminación?</span>
                <button onClick={handleDeleteDataset} style={{ ...S.btn, ...S.btnDanger }}>Sí, eliminar</button>
                <button onClick={() => setConfirmDelete(false)} style={{ ...S.btn, ...S.btnGhost }}>Cancelar</button>
              </>
            )}
            <button onClick={() => fileRef.current?.click()} disabled={saving}
              style={{ ...S.btn, ...S.btnPrimary }}>
              {dataset ? '↻ Reemplazar Excel' : '⬆ Cargar Excel'}
            </button>
            <button onClick={onClose} style={{ ...S.btn, ...S.btnGhost }}>✕ Cerrar</button>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div style={{ margin: '12px 24px 0', padding: '8px 14px', borderRadius: 8, background: toast.ok ? '#d4edda' : '#f8d7da', color: toast.ok ? '#155724' : '#721c24', fontSize: 12, fontWeight: 600 }}>
            {toast.ok ? '✅' : '❌'} {toast.msg}
          </div>
        )}

        {/* Body */}
        <div style={S.body}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-tertiary)', fontSize: 13 }}>Cargando dataset…</div>
          ) : !dataset ? (
            /* Empty state */
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 8 }}>Sin dataset configurado</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24, maxWidth: 420, margin: '0 auto 24px' }}>
                Carga un archivo Excel para configurar el Data Driven Testing. Cada fila generará una ejecución independiente del caso de prueba.
              </div>
              <button onClick={() => fileRef.current?.click()} style={{ ...S.btn, ...S.btnPrimary, fontSize: 13, padding: '10px 20px' }}>
                ⬆ Seleccionar archivo Excel
              </button>
              <div style={{ marginTop: 16, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                Formatos soportados: .xlsx, .xls · La primera fila se usa como encabezados de columna
              </div>
            </div>
          ) : (
            /* Dataset table */
            <div>
              {/* Info bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: '#e8f4fd', color: '#185FA5', fontWeight: 600 }}>
                    {dataset.columns.length} columnas
                  </span>
                  <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: '#e6f9ee', color: '#065f46', fontWeight: 600 }}>
                    {dataset.active_row_count} de {dataset.row_count} filas activas = {dataset.active_row_count} ejecuciones por caso
                  </span>
                </div>
                <button onClick={addRow} disabled={saving}
                  style={{ ...S.btn, ...S.btnGhost, fontSize: 12 }}>
                  + Agregar fila
                </button>
              </div>

              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
                💡 Doble clic en una celda para editar · Enter o Tab para confirmar · Esc para cancelar · Desmarca una fila para excluirla de la ejecución sin borrar sus datos
              </div>

              {/* Table */}
              <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid var(--color-border-secondary)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--color-background-secondary)' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: 11, color: 'var(--color-text-tertiary)', width: 40, borderBottom: '1px solid var(--color-border-secondary)' }}>#</th>
                      <th title="Fila activa para ejecución" style={{ padding: '8px 6px', textAlign: 'center', fontSize: 11, color: 'var(--color-text-tertiary)', width: 32, borderBottom: '1px solid var(--color-border-secondary)' }}>✓</th>
                      {dataset.columns.map(col => (
                        <th key={col} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--color-text-primary)', borderBottom: '1px solid var(--color-border-secondary)', whiteSpace: 'nowrap' }}>
                          {col}
                        </th>
                      ))}
                      <th style={{ width: 36, borderBottom: '1px solid var(--color-border-secondary)' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {dataset.rows.map((row, ri) => {
                      const isActive = dataset.active_rows[ri] ?? true;
                      return (
                      <tr key={ri} style={{ borderBottom: '1px solid var(--color-border-tertiary)', opacity: isActive ? 1 : 0.45 }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-background-secondary)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        {/* Row number */}
                        <td style={{ padding: '6px 10px', textAlign: 'center', fontSize: 11, color: 'var(--color-text-tertiary)' }}>{ri + 1}</td>

                        {/* Active checkbox */}
                        <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                          <input type="checkbox" checked={isActive} onChange={() => toggleRowActive(ri)}
                            title={isActive ? 'Excluir de la ejecución' : 'Incluir en la ejecución'}
                            style={{ cursor: 'pointer' }} />
                        </td>

                        {/* Cells */}
                        {dataset.columns.map((col, ci) => {
                          const isEditing = editingCell?.row === ri && editingCell?.col === ci;
                          const isSaving = savingCell?.row === ri && savingCell?.col === ci;
                          const cellVal = row[ci] ?? '';

                          return (
                            <td key={ci} style={{ padding: '4px 6px', maxWidth: 200, position: 'relative' }}
                              onDoubleClick={() => !isEditing && startEdit(ri, ci, cellVal)}>
                              {isEditing ? (
                                <input
                                  autoFocus
                                  value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  onBlur={() => commitEdit(ri, ci, editValue)}
                                  onKeyDown={e => handleCellKeyDown(e, ri, ci)}
                                  style={{ width: '100%', padding: '4px 8px', borderRadius: 6, border: '2px solid #185FA5', background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' as const }}
                                />
                              ) : (
                                <div style={{ padding: '4px 8px', borderRadius: 6, border: '2px solid transparent', cursor: 'cell', minHeight: 28, display: 'flex', alignItems: 'center', gap: 4, transition: 'border-color 0.1s' }}
                                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--color-border-secondary)')}
                                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'transparent')}>
                                  {isSaving ? (
                                    <span style={{ fontSize: 11, color: '#22863a' }}>✓</span>
                                  ) : null}
                                  <span style={{ color: cellVal ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)', fontStyle: cellVal ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {cellVal || '(vacío)'}
                                  </span>
                                </div>
                              )}
                            </td>
                          );
                        })}

                        {/* Delete row */}
                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                          <button onClick={() => deleteRow(ri)} disabled={saving}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--color-text-tertiary)', padding: '2px 4px', borderRadius: 4, opacity: 0.6 }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#dc3545'; (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--color-text-tertiary)'; (e.currentTarget as HTMLElement).style.opacity = '0.6'; }}>
                            ✕
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {dataset && (
          <div style={{ padding: '12px 24px', borderTop: '1px solid var(--color-border-secondary)', fontSize: 11, color: 'var(--color-text-tertiary)', flexShrink: 0 }}>
            ⚡ Cada ejecución generará <strong>{dataset.active_row_count} run{dataset.active_row_count !== 1 ? 's' : ''}</strong> por caso de prueba que tenga mapeo configurado en este escenario
            {dataset.active_row_count === 0 && ' — sin filas activas, se ejecutará con los valores fijos definidos en las acciones'}.
          </div>
        )}
      </div>

      <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileChange} />
    </div>
  );
}